import { ReactElement, useContext, useMemo } from 'react';
import { useMatch, useParams } from 'react-router-dom';
import {
  CloudOutlined as CloudIcon,
  RocketLaunch as FlightTakeoffIcon,
  Extension as ExtensionIcon,
  Lock as AdminIcon,
  DonutLarge as LayersIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  Timelapse as TimelapseIcon,
  Add as AddIcon,
} from '@mui/icons-material';

import {
  buildRoute,
  GlobalStateContext,
  useProductArea,
  useHasOnlySnowflakeAccess,
  useDisableFeatures,
} from '@localstack/services';
import { LocalStackInstanceType, LocalStackInstance } from '@localstack/types';
import { ProductArea } from '@localstack/constants';

import { AppRoute } from '~/config';
import { useAuthProvider } from '~/hooks/useAuthProvider';
import { BlogCardViewer } from '~/layouts/components/Blog/BlogCardViewer';

import { WorkspaceSelector } from './WorkspaceSelector';
import { NavbarContainer, NavbarSection, SectionHeader, SectionItem } from './shared';
import { NavbarCloseButton } from './NavbarCloseButton';
import { getDisplayUrl } from './utils';

// Component for individual instance bookmark item
const InstanceBookmarkItem = ({
  instance,
  iid,
  hasOnlySnowflakeTrial,
}: {
  instance: LocalStackInstance;
  iid: string;
  hasOnlySnowflakeTrial: boolean;
}) => {
  const enableAction = useDisableFeatures({ endpoint: instance.endpoint });

  const StatusIcon = enableAction ? RadioButtonCheckedIcon : RadioButtonUncheckedIcon;

  return (
    <SectionItem
      key={instance.id}
      label={getDisplayUrl(instance.endpoint)}
      icon={(props) => (
        <StatusIcon
          {...props}
          sx={{
            color: (theme) => (enableAction ? theme.palette.success.main : theme.palette.grey[600]),
            fontSize: '18px',
          }}
        />
      )}
      to={buildRoute(hasOnlySnowflakeTrial ? AppRoute.INSTANCE_SNOWFLAKE : AppRoute.RESOURCES_OVERVIEW, {
        iid: instance.id,
      })}
      isActive={instance.id === iid}
    />
  );
};

/** Contains menu items to be shown with the main actions of the app */
export const MainNavbar = (): ReactElement => {
  const { instances } = useContext(GlobalStateContext);
  const { iid } = useParams<'iid'>() as { iid: string };

  const { isPlatformAdmin } = useAuthProvider();

  const podsMatch = useMatch(AppRoute.PODS);
  const podMatch = useMatch(AppRoute.POD);
  const instancesMatch = useMatch(AppRoute.INSTANCES);
  const instancesEphemeralMatch = useMatch(AppRoute.INSTANCES_EPHEMERAL);

  const isCloudPodsPage = useMemo(() => !!podsMatch || !!podMatch, [podsMatch, podMatch]);
  const isInstancesManagementPage = useMemo(
    () => !!instancesMatch || !!instancesEphemeralMatch,
    [instancesMatch, instancesEphemeralMatch],
  );

  const hasOnlySnowflakeSub = useHasOnlySnowflakeAccess({ useAuthProvider });
  const hasAccessToProductArea = useProductArea({ useAuthProvider });

  // The heading is only worth drawing when at least one item under it survives, so both come
  // from one list rather than two that have to be kept in agreement.
  const workspaceItems = [
    {
      label: 'Cloud Pods',
      icon: CloudIcon,
      to: AppRoute.PODS,
      area: ProductArea.CLOUD_PODS,
      isActive: isCloudPodsPage,
    },
    {
      label: 'Stack Insights',
      icon: LayersIcon,
      to: AppRoute.STACKS_OVERVIEW,
      area: ProductArea.STACK_INSIGHTS,
    },
    {
      label: 'Extensions',
      icon: ExtensionIcon,
      to: AppRoute.EXTENSIONS_LIBRARY,
      area: ProductArea.EXTENSIONS,
    },
  ].filter(({ area }) => hasAccessToProductArea(area));

  const regularInstances = instances.filter(
    (instance) => instance.instanceType === LocalStackInstanceType.REGULAR_INSTANCE,
  );
  const ephemeralInstances = instances.filter(
    (instance) => instance.instanceType === LocalStackInstanceType.EPHEMERAL_INSTANCE,
  );

  return (
    <NavbarContainer>
      <NavbarContainer>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <WorkspaceSelector />
          <NavbarCloseButton />
        </div>
        {isPlatformAdmin && <SectionItem label="Admin Panel" icon={AdminIcon} to={AppRoute.ADMIN_HOME} />}
        <NavbarSection>
          <SectionItem label="Getting Started" icon={FlightTakeoffIcon} to={AppRoute.GETTING_STARTED} />
        </NavbarSection>
        {hasAccessToProductArea(ProductArea.INSTANCES) && (
          <NavbarSection data-name="stacks sub navigation">
            <SectionHeader>Instances</SectionHeader>

            {/* Regular instance bookmarks */}
            {regularInstances.map((instance) => (
              <InstanceBookmarkItem
                key={instance.id}
                instance={instance}
                iid={iid}
                hasOnlySnowflakeTrial={hasOnlySnowflakeSub}
              />
            ))}

            {/* Ephemeral instances */}
            {ephemeralInstances.map((instance) => (
              <SectionItem
                key={instance.id}
                label={getDisplayUrl(instance.endpoint)}
                icon={(props) => (
                  <TimelapseIcon
                    {...props}
                    sx={{
                      color: (theme) => theme.palette.success.main,
                      fontSize: '18px',
                    }}
                  />
                )}
                to={buildRoute(hasOnlySnowflakeSub ? AppRoute.INSTANCE_SNOWFLAKE : AppRoute.RESOURCES_OVERVIEW, {
                  iid: instance.id,
                })}
                isActive={instance.id === iid}
              />
            ))}

            <SectionItem
              label="New Instance"
              icon={(props) => (
                <AddIcon
                  {...props}
                  sx={{
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: '18px',
                  }}
                />
              )}
              to={AppRoute.INSTANCES}
              isActive={isInstancesManagementPage}
              end
              sx={{ color: (theme) => theme.palette.text.secondary }}
            />
          </NavbarSection>
        )}
        {workspaceItems.length > 0 && (
          <NavbarSection data-name="workspace sub navigation">
            <SectionHeader>Workspace</SectionHeader>

            {workspaceItems.map(({ area, ...item }) => (
              <SectionItem key={area} {...item} />
            ))}
          </NavbarSection>
        )}
      </NavbarContainer>
      <BlogCardViewer />
    </NavbarContainer>
  );
};
